// wizardFactory.ts
import { AbstractWizard } from './AbstractWizard.js';
import { WizardConfig } from './type.js';
import { travelWizardConfig } from './wizardConfigs/travelConfig.js';
import { restaurantWizardConfig } from './wizardConfigs/restaurantConfig.js';
import { onboardingWizardConfig } from './wizardConfigs/onboardingConfig.js';



interface FieldDefinition {
  name: string;
  required: boolean;
  defaultValue: any;
  description?: string;
}

export interface IntentDetectionResult {
  wizardType: string;
  confidence: number;
    possibleFields: string[]; // This should be string[], not FieldDefinition[]
}


export class WizardFactory {
  static readonly WIZARD_TYPES = {
    TRAVEL: 'travel',
    CUSTOM: 'custom',
    RESTAURANT: 'restaurant',
    ONBOARDING: 'onboarding'
  };

  static createWizard(type: string, userId: string, customConfig?: WizardConfig): AbstractWizard {
    let config: WizardConfig;
    
    switch(type) {
      case this.WIZARD_TYPES.ONBOARDING:
        config = onboardingWizardConfig;
        break;
      case this.WIZARD_TYPES.TRAVEL:
        config = travelWizardConfig;
        break;
      case this.WIZARD_TYPES.RESTAURANT:
      config = restaurantWizardConfig;
      break;
      case this.WIZARD_TYPES.CUSTOM:
        if (!customConfig) throw new Error('Custom config required for custom wizard');
        config = customConfig;
        break;
      default:
        throw new Error(`Unknown wizard type: ${type}`);
    }
    
    return new AbstractWizard(userId, config);
  }

  static async detectIntent(message: string): Promise<IntentDetectionResult> {
    try {
      const response = await fetch('/api/wizard/detect-intent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ message })
      });
      
      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error("Intent detection error:", error);
      return { 
        wizardType: 'other', 
        confidence: 0.1,
        possibleFields: []
      };
    }
  }

/*   static async detectIntent_UNUSED2(message: string): Promise<IntentDetectionResult> {
    const apiKey = process.env.CLAUDE_API_KEY;
    const systemPrompt = `Analyze the user message and determine the intent category.
      Available categories:
      - "travel": Trip planning, vacations, destinations
      - "restaurant": Dining, reservations, food
      - "automotive": Cars, vehicles, buying/selling
      - "other": None of the above
      
      Respond with JSON:
      {
        "wizardType": "travel|restaurant|automotive|other",
        "confidence": 0.0-1.0,
        "possibleFields": []
      }`;
  
    // Better fallback that doesn't assume travel
    if (!apiKey) return { 
      wizardType: 'other', 
      confidence: 0.1,
      possibleFields: []
    };
    
    try {
      // Call to API remains the same
      const res = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'x-api-key': apiKey,
          'anthropic-version': '2023-06-01',
          'content-type': 'application/json'
        },
        body: JSON.stringify({
          model: 'claude-3-haiku-20240307',
          system: systemPrompt,
          messages: [{ role: 'user', content: message }],
          max_tokens: 200,
          temperature: 0
        })
      });
      
      const data = await res.json();
      const text = data.content[0].text;
      
      // Add logging for the raw response
      console.log("Intent detection response:", text);
      
      // Extract JSON from the text
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const result = JSON.parse(jsonMatch[0]);
        //console.log("Detected intent:", result);
        return result;
      }
      
      // Better fallback
      console.log("Could not extract JSON from intent detection");
      return { 
        wizardType: 'other', 
        confidence: 0.2,
        possibleFields: []
      };
    } catch (error) {
      console.error("Intent detection error:", error);
      return { 
        wizardType: 'other', 
        confidence: 0.1,
        possibleFields: []
      };
    }
  } */
/*   static async detectIntent_UNUSED(message: string): Promise<IntentDetectionResult> {
    const apiKey = process.env.CLAUDE_API_KEY;
    const systemPrompt = `Analyze the user message and determine the intent category.
      Available categories:
      - "travel": Trip planning, vacations, destinations
      - "restaurant": Dining, reservations, food
      - "automotive": Cars, vehicles, buying/selling
      - "other": None of the above
      
      Respond with JSON:
      {
        "wizardType": "travel|restaurant|automotive|other",
        "confidence": 0.0-1.0,
        "possibleFields": []
      }`;

    if (!apiKey) return { 
      wizardType: 'travel', 
      confidence: 0.5,
      possibleFields: []
    };
    
    try {
      const res = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'x-api-key': apiKey,
          'anthropic-version': '2023-06-01',
          'content-type': 'application/json'
        },
        body: JSON.stringify({
          model: 'claude-3-haiku-20240307',
          system: systemPrompt,
          messages: [{ role: 'user', content: message }],
          max_tokens: 200,
          temperature: 0
        })
      });
      
      const data = await res.json();
      const text = data.content[0].text;
      
      // Extract JSON from the text
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }
      
      return { 
        wizardType: 'travel', 
        confidence: 0.5,
        possibleFields: []
      };
    } catch (error) {
      console.error("Intent detection error:", error);
      return { 
        wizardType: 'travel', 
        confidence: 0.5,
        possibleFields: []
      };
    }
  } */

  static createIntentSpecificWizard(userId: string, intent: string, possibleFields: string[]): AbstractWizard {
    // Normalize field names
    const normalizedFields = possibleFields.map(field => ({
      name: field.replace(/\s+/g, '_').toLowerCase(),
      original: field
    }));
    
    // Create field definitions
    const fieldDefinitions: FieldDefinition[] = normalizedFields.map(field => ({
      name: field.name,
      required: true,
      defaultValue: null,
      description: `${field.original}`
    }));
    
    // Create customized prompts based on intent
    let extractionPrompt = '';
    let completionPrompt = '';
    
    // Customize prompts based on intent
    switch(intent) {
      case 'automotive':
        extractionPrompt = `Extract car purchasing information from conversations.
        Look for details about:
        ${fieldDefinitions.map(f => `- ${f.name}: Information about the ${f.description}`).join('\n')}
        
        Output your response in valid JSON format.`;
        
        completionPrompt = `Create a brief, friendly summary of car requirements.
        Keep it under 50 words, be conversational, and confirm you'll help with their car search.`;
        break;
      
      default:
        extractionPrompt = `Extract structured information about ${intent} from conversations.
        Look for details about:
        ${fieldDefinitions.map(f => `- ${f.name}: Information about ${f.description}`).join('\n')}
        
        Output your response as a valid JSON object with these fields.`;
        
        completionPrompt = `Create a brief, friendly summary of the information collected.
        Keep it conversational and confirm you'll help with this request.`;
    }
    
    // Create the dynamic config
    const dynamicConfig: WizardConfig = {
      id: intent,
      name: `${intent.charAt(0).toUpperCase() + intent.slice(1)} Wizard`,
      fieldDefinitions,
      extractionPrompt,
      completionPrompt
    };
    
    return new AbstractWizard(userId, dynamicConfig);
  }

  static createDynamicWizard(userId: string, message: string, possibleFields?: string[]): AbstractWizard {
    // Safely handle undefined or empty possibleFields
    const fieldsToUse: string[] = Array.isArray(possibleFields) && possibleFields.length > 0
      ? possibleFields
      : ['destination', 'startDate', 'endDate', 'travelGroup', 'interests'];
    
    // Normalize field names
    const normalizedFields = fieldsToUse.map(field => ({
      name: typeof field === 'string' ? field.replace(/\s+/g, '_').toLowerCase() : 'field',
      original: field || 'Field'
    }));
    
    // Create field definitions
    const fieldDefinitions = normalizedFields.map(field => ({
      name: field.name,
      required: true,
      defaultValue: null,
      description: `The ${field.original}`
    }));
    
    // Create a dynamic config based on the message
    const dynamicConfig = {
      id: 'dynamic',
      name: 'Dynamic Wizard',
      fieldDefinitions,
      extractionPrompt: `Extract structured information from conversations in JSON format.
        Look for details about:
        ${fieldDefinitions.map(f => `- ${f.name}: Any mention of ${f.description}`).join('\n')}
        
        Output your response as a valid JSON object with these fields.
        For missing information, use null.`,
      completionPrompt: `Create a brief, friendly summary of the information collected:
        ${fieldDefinitions.map(f => `- ${f.name}: {{${f.name}}}`).join('\n')}
        
        Keep it under 50 words, be conversational, and confirm you'll help with this request.`
    };
    
    return new AbstractWizard(userId, dynamicConfig);
  }
/*   static createDynamicWizard_UNUSED2(userId: string, message: string, possibleFields?: string[]): AbstractWizard {

     // Always initialize possibleFields to avoid the mapping error
    const fieldsToUse = possibleFields?.length > 0 
      ? possibleFields 
      : ['destination', 'startDate', 'endDate', 'travelGroup', 'interests'];

    // Now use fieldsToUse which is guaranteed to be an array
    const normalizedFields = fieldsToUse.map(field => ({
      name: field.replace(/\s+/g, '_').toLowerCase(),
      original: field
    }));
    
    // Convert the detected fields into field definitions
    const fieldDefinitions: FieldDefinition[] = normalizedFields.map(field => ({
      name: field.name,
      required: true,
      defaultValue: null,
      description: `The ${field.original} mentioned in the conversation`
    }));
    
    // Create a dynamic config based on the message and intent
    const dynamicConfig: WizardConfig = {
      id: 'dynamic',
      name: 'Dynamic Wizard',
      fieldDefinitions,
      extractionPrompt: `Extract structured information from conversations in JSON format.
      Look for details about:
      ${fieldDefinitions.map(f => `- ${f.name}: Any mention of ${f.description}`).join('\n')}
      
      Output your response as a valid JSON object with these fields.
      For missing information, use null.`,
      completionPrompt: `Create a brief, friendly summary of the information collected:
      ${fieldDefinitions.map(f => `- ${f.name}: {{${f.name}}}`).join('\n')}
      
      Keep it under 50 words, be conversational, and confirm you'll help with this request.`
    };
    
    return new AbstractWizard(userId, dynamicConfig);
  }

  static createDynamicWizard_UNUSED1(userId: string, message: string, possibleFields: string[]): AbstractWizard {
    // Convert the detected fields into field definitions
    const fieldDefinitions: FieldDefinition[] = possibleFields.map(name => ({
      name,
      required: true,
      defaultValue: null,
      description: `The ${name} mentioned in the conversation`
    }));
    
    // Add some common fields if we have too few detected fields
    if (fieldDefinitions.length < 3) {
      if (!possibleFields.includes('date')) {
        fieldDefinitions.push({
          name: 'date',
          required: true,
          defaultValue: null,
          description: 'When this should happen'
        });
      }
      
      if (!possibleFields.includes('details')) {
        fieldDefinitions.push({
          name: 'details',
          required: false,
          defaultValue: [],
          description: 'Additional details or preferences'
        });
      }
    }
    
    // Create a dynamic config based on the message
    const dynamicConfig: WizardConfig = {
      id: 'dynamic',
      name: 'Dynamic Wizard',
      fieldDefinitions,
      extractionPrompt: `Extract structured information from conversations in JSON format.
      Look for details about:
      ${fieldDefinitions.map(f => `- ${f.name}: Any mention of ${f.name}`).join('\n')}
      
      Output your response as a valid JSON object with these fields.
      For missing information, use null.`,
      completionPrompt: `Create a brief, friendly summary of the information collected:
      ${fieldDefinitions.map(f => `- ${f.name}: {{${f.name}}}`).join('\n')}
      
      Keep it under 50 words, be conversational, and confirm you'll help with this request.`
    };
    
    return new AbstractWizard(userId, dynamicConfig);
  }
 */

}